import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  CalendarBlank, Clock, MapPin, MagnifyingGlass, FileText,
  DownloadSimple, Printer, ArrowsCounterClockwise, Funnel, X
} from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();

export default function SharedRSVPViewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('attending'); // default to Attending tab
  const [ageFilter, setAgeFilter] = useState('all'); // all | 0-3 | 4-6 | 7+

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/events/share/${token}`);
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load share link.');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const event = data?.event;
  const themeColor = event?.theme_color || '#FF6B35';

  // Parse a sibling response into a uniform list of {name, age?}
  const parseSiblings = (cr, cf) => {
    const raw = cr?.[cf.field_id];
    if (cf.field_type === 'siblings') {
      if (raw && typeof raw === 'object' && Array.isArray(raw.list)) {
        if ((raw.attending || '').toLowerCase() === 'no') return [];
        return raw.list.filter(s => (s.name || '').trim() || (s.age ?? '') !== '');
      }
      return [];
    }
    return [];
  };

  // Detect structured siblings field (new) or legacy yes/no + name pair (old)
  const siblingsMeta = useMemo(() => {
    const cfs = data?.event?.custom_fields || [];
    const structured = cfs.find(cf => cf.field_type === 'siblings');
    if (structured) return { kind: 'structured', fieldId: structured.field_id, label: structured.label };
    const yesNo = cfs.find(cf => /sibling/i.test(cf.label || '') && (cf.field_type === 'yesno' || cf.field_type === 'radio' || cf.field_type === 'dropdown'));
    const nameField = cfs.find(cf => /sibling/i.test(cf.label || '') && /(name|age|who)/i.test(cf.label || '') && cf.field_type !== 'yesno');
    return { kind: 'legacy', yesNoId: yesNo?.field_id || null, nameId: nameField?.field_id || null };
  }, [data]);

  // Returns list of siblings per RSVP for either structured or legacy shape
  const siblingsForRsvp = (r) => {
    const cr = r.custom_responses || {};
    if (siblingsMeta.kind === 'structured') {
      const cf = (data?.event?.custom_fields || []).find(c => c.field_id === siblingsMeta.fieldId);
      return parseSiblings(cr, cf || { field_id: siblingsMeta.fieldId, field_type: 'siblings' });
    }
    // Legacy: parse name string into list w/ best-effort age extraction
    const yn = siblingsMeta.yesNoId ? String(cr[siblingsMeta.yesNoId] || '').toLowerCase() : '';
    const nameVal = siblingsMeta.nameId ? String(cr[siblingsMeta.nameId] || '').trim() : '';
    if (!nameVal && !yn.includes('yes')) return [];
    if (!nameVal && yn.includes('yes')) return [{ name: '—', age: null }];
    const parts = nameVal.split(/\n|;|,| and | & /i).map(s => s.trim()).filter(Boolean);
    return parts.map(p => {
      const m = p.match(/^(.*?)[\s-]+(\d+(?:\.\d+)?)\s*(?:y|yr|yrs|years?)?\s*$/i);
      if (m) return { name: m[1].trim(), age: parseFloat(m[2]) };
      return { name: p, age: null };
    });
  };

  const siblingsCount = useMemo(() => {
    if (!data) return 0;
    let kids = 0;
    for (const r of data.rsvps) {
      if (!(r.can_attend || '').toLowerCase().includes('yes')) continue;
      kids += siblingsForRsvp(r).length;
    }
    return kids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, siblingsMeta]);

  const ageBucket = (age) => {
    if (age == null || isNaN(age)) return 'unknown';
    if (age <= 3) return '0-3';
    if (age <= 6) return '4-6';
    return '7+';
  };

  const filteredRsvps = useMemo(() => {
    if (!data) return [];
    let rows = data.rsvps;
    if (filter === 'attending') rows = rows.filter(r => (r.can_attend || '').toLowerCase().includes('yes'));
    if (filter === 'not_attending') rows = rows.filter(r => !(r.can_attend || '').toLowerCase().includes('yes'));
    if (ageFilter !== 'all') {
      rows = rows.filter(r => siblingsForRsvp(r).some(s => ageBucket(s.age) === ageFilter));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r => {
        const sibStr = siblingsForRsvp(r).map(s => `${s.name || ''} ${s.age ?? ''}`).join(' ');
        const blob = [
          r.child_name, r.parent_name, r.parent_phone,
          (r.accompanying || []).join(' '), r.dietary, sibStr,
          ...Object.values(r.custom_responses || {}).map(v => {
            if (Array.isArray(v)) return v.join(' ');
            if (v && typeof v === 'object') return JSON.stringify(v);
            return String(v || '');
          }),
        ].join(' ').toLowerCase();
        return blob.includes(q);
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filter, search, ageFilter, siblingsMeta]);

  const downloadShare = (kind) => {
    const a = document.createElement('a');
    a.href = `${API}/api/events/share/${token}/export/${kind}`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="w-8 h-8 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-neutral-50 text-center">
      <div className="text-5xl mb-4">🔒</div>
      <h1 className="text-xl font-bold text-neutral-900 mb-2">Link not available</h1>
      <p className="text-sm text-neutral-600 max-w-sm">{error || 'This share link is invalid, expired, or has been revoked by the host.'}</p>
    </div>
  );

  const customFields = event.custom_fields || [];

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 print:bg-white" data-testid="shared-rsvp-view">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white sticky top-0 z-30 print:static print:border-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {event.theme_emoji && <span className="text-3xl shrink-0">{event.theme_emoji}</span>}
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold mb-0.5" style={{ color: themeColor }}>Live RSVP View</div>
                <h1 className="text-lg sm:text-xl font-bold truncate" data-testid="shared-event-title">{event.title}</h1>
                <div className="flex flex-wrap gap-3 text-xs text-neutral-600 mt-1">
                  {event.event_date && <span><CalendarBlank size={12} className="inline mr-1" />{event.event_date}</span>}
                  {event.event_time && <span><Clock size={12} className="inline mr-1" />{event.event_time}{event.end_time ? `-${event.end_time}` : ''}</span>}
                  {event.venue && <span className="truncate max-w-[280px]"><MapPin size={12} className="inline mr-1" />{event.venue}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 print:hidden">
              <button onClick={() => load()} data-testid="refresh-share-btn"
                className="px-2.5 py-2 rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200 text-xs font-semibold inline-flex items-center gap-1.5">
                <ArrowsCounterClockwise size={12} weight="bold" /> Refresh
              </button>
              <button onClick={() => downloadShare('xlsx')} data-testid="dl-xlsx"
                className="px-2.5 py-2 rounded-lg text-white text-xs font-bold inline-flex items-center gap-1.5"
                style={{ background: themeColor }}>
                <DownloadSimple size={12} weight="bold" /> Excel
              </button>
              <button onClick={() => downloadShare('csv')} data-testid="dl-csv"
                className="px-2.5 py-2 rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200 text-xs font-semibold inline-flex items-center gap-1.5">
                <FileText size={12} /> CSV
              </button>
              <button onClick={() => downloadShare('pdf')} data-testid="dl-pdf"
                className="px-2.5 py-2 rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200 text-xs font-semibold inline-flex items-center gap-1.5">
                <FileText size={12} /> PDF
              </button>
              <button onClick={() => window.print()} data-testid="print-btn"
                className="px-2.5 py-2 rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200 text-xs font-semibold inline-flex items-center gap-1.5">
                <Printer size={12} /> Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats + Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <StatCard label="Total" value={data.counts.total} color={themeColor} testid="stat-total" />
          <StatCard label="Attending" value={data.counts.attending} color="#10B981" testid="stat-attending" />
          <StatCard label="Not Attending" value={data.counts.not_attending} color="#EF4444" testid="stat-not-attending" />
          <StatCard label="Siblings Attending" value={siblingsCount} color="#6366F1" testid="stat-siblings" />
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap print:hidden">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              data-testid="share-search-input"
              placeholder="Search name, phone, dietary, custom answer…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-neutral-200 text-xs outline-none focus:border-neutral-400" />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
                <X size={12} />
              </button>
            )}
          </div>
          <div className="inline-flex rounded-lg bg-neutral-100 p-0.5" data-testid="share-filter-group">
            {[
              { id: 'all', label: 'All' },
              { id: 'attending', label: 'Attending' },
              { id: 'not_attending', label: "Can't make it" },
            ].map(t => (
              <button key={t.id} onClick={() => setFilter(t.id)}
                data-testid={`filter-${t.id}`}
                className={`px-2.5 py-1.5 rounded-md text-[11px] font-bold transition-all ${filter === t.id ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-600 hover:text-neutral-900'}`}>
                {t.label}
              </button>
            ))}
          </div>
          {siblingsCount > 0 && (
            <div className="inline-flex rounded-lg bg-neutral-100 p-0.5" data-testid="age-filter-group" title="Filter by sibling age">
              {[
                { id: 'all', label: 'All ages' },
                { id: '0-3', label: '0–3' },
                { id: '4-6', label: '4–6' },
                { id: '7+', label: '7+' },
              ].map(t => (
                <button key={t.id} onClick={() => setAgeFilter(t.id)}
                  data-testid={`age-filter-${t.id}`}
                  className={`px-2.5 py-1.5 rounded-md text-[11px] font-bold transition-all ${ageFilter === t.id ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-600 hover:text-neutral-900'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Mobile: card-per-RSVP (sm:hidden) */}
        <div className="sm:hidden space-y-2" data-testid="share-cards-mobile">
          {filteredRsvps.length === 0 ? (
            <div className="bg-white rounded-xl border border-neutral-200 text-center py-10 text-neutral-500 text-sm">
              {data.rsvps.length === 0 ? 'No RSVPs yet.' : 'No matches for the current filter.'}
            </div>
          ) : (
            filteredRsvps.map((r, i) => {
              const attending = (r.can_attend || '').toLowerCase().includes('yes');
              return (
                <div key={r.rsvp_id} className="bg-white rounded-xl border border-neutral-200 px-3.5 py-3"
                  data-testid={`rsvp-card-${r.rsvp_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-neutral-400 tabular-nums">#{i + 1}</span>
                        <span className="text-[15px] font-bold text-neutral-900 truncate">{r.child_name}</span>
                      </div>
                      {r.parent_name && <div className="text-[11px] text-neutral-500 mt-0.5 truncate">by {r.parent_name}</div>}
                    </div>
                    <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${attending ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {attending ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                  {r.parent_phone && (
                    <div className="text-[11px] text-neutral-600 tabular-nums">📞 {r.parent_phone}</div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(r.accompanying || []).map((a, ai) => (
                      <span key={ai} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-neutral-100 text-neutral-700">{a}</span>
                    ))}
                    {r.dietary && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">🍽 {r.dietary}</span>
                    )}
                  </div>
                  {customFields.length > 0 && (
                    <div className="mt-2.5 pt-2.5 border-t border-neutral-100 space-y-1.5">
                      {customFields.map(cf => {
                        const v = (r.custom_responses || {})[cf.field_id];
                        if (cf.field_type === 'image') {
                          if (!v) return null;
                          return <CardKV key={cf.field_id} label={cf.label}><span className="text-emerald-600 font-bold text-[11px]">✓ uploaded</span></CardKV>;
                        }
                        if (cf.field_type === 'siblings') {
                          const sibs = parseSiblings(r.custom_responses || {}, cf);
                          if (sibs.length === 0) {
                            const attend = (v && typeof v === 'object') ? (v.attending || '') : '';
                            if (attend === 'No') return <CardKV key={cf.field_id} label={cf.label}><span className="text-[11px] text-neutral-500">No</span></CardKV>;
                            return null;
                          }
                          return (
                            <CardKV key={cf.field_id} label={cf.label}>
                              <div className="flex flex-wrap gap-1">
                                {sibs.map((s, idx) => (
                                  <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                                    {s.name || '—'}{s.age != null && s.age !== '' ? ` · ${s.age}` : ''}
                                  </span>
                                ))}
                              </div>
                            </CardKV>
                          );
                        }
                        const txt = Array.isArray(v) ? v.join(', ') : (v && typeof v === 'object' ? '' : (v || ''));
                        if (!txt) return null;
                        return <CardKV key={cf.field_id} label={cf.label}><span className="text-[11px] text-neutral-700">{txt}</span></CardKV>;
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Desktop / tablet: data table (hidden on mobile) */}
        <div className="hidden sm:block bg-white rounded-xl border border-neutral-200 overflow-hidden print:border-0 print:rounded-none print:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="share-table">
              <thead style={{ background: themeColor }}>
                <tr className="text-white">
                  <Th>#</Th>
                  <Th>Child</Th>
                  <Th>Parent</Th>
                  <Th>Phone</Th>
                  <Th>Status</Th>
                  <Th>Accompanying</Th>
                  <Th>Dietary</Th>
                  {customFields.map(cf => <Th key={cf.field_id}>{cf.label}</Th>)}
                </tr>
              </thead>
              <tbody>
                {filteredRsvps.length === 0 ? (
                  <tr>
                    <td colSpan={7 + customFields.length} className="text-center py-12 text-neutral-500">
                      {data.rsvps.length === 0 ? 'No RSVPs yet.' : 'No matches for the current filter.'}
                    </td>
                  </tr>
                ) : (
                  filteredRsvps.map((r, i) => {
                    const attending = (r.can_attend || '').toLowerCase().includes('yes');
                    return (
                      <tr key={r.rsvp_id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`rsvp-row-${r.rsvp_id}`}>
                        <Td className="text-neutral-400 tabular-nums">{i + 1}</Td>
                        <Td className="font-semibold">{r.child_name}</Td>
                        <Td>{r.parent_name || <Empty />}</Td>
                        <Td className="tabular-nums">{r.parent_phone || <Empty />}</Td>
                        <Td>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${attending ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {attending ? '✓ Yes' : '✗ No'}
                          </span>
                        </Td>
                        <Td>{(r.accompanying || []).join(', ') || <Empty />}</Td>
                        <Td>{r.dietary || <Empty />}</Td>
                        {customFields.map(cf => {
                          const v = (r.custom_responses || {})[cf.field_id];
                          if (cf.field_type === 'image') {
                            return <Td key={cf.field_id}>{v ? <span className="text-emerald-600 font-bold">✓ uploaded</span> : <Empty />}</Td>;
                          }
                          if (cf.field_type === 'siblings') {
                            const sibs = parseSiblings(r.custom_responses || {}, cf);
                            if (sibs.length === 0) {
                              const attend = (v && typeof v === 'object') ? (v.attending || '') : '';
                              return <Td key={cf.field_id}>{attend === 'No' ? <span className="text-neutral-500">No</span> : <Empty />}</Td>;
                            }
                            return (
                              <Td key={cf.field_id}>
                                <div className="flex flex-wrap gap-1">
                                  {sibs.map((s, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                                      {s.name || '—'}{s.age != null && s.age !== '' ? ` · ${s.age}` : ''}
                                    </span>
                                  ))}
                                </div>
                              </Td>
                            );
                          }
                          const txt = Array.isArray(v) ? v.join(', ') : (v && typeof v === 'object' ? '' : (v || ''));
                          return <Td key={cf.field_id}>{txt || <Empty />}</Td>;
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-[10px] text-neutral-500 mt-3 text-center print:hidden">
          Live read-only view · updates when you click Refresh ·
          {event.share_expires_at ? <> link expires {new Date(event.share_expires_at).toLocaleDateString()}</> : ' link never expires'}
        </p>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1">{label}</div>
      <div className="text-2xl sm:text-3xl font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left text-[11px] font-bold uppercase tracking-wider px-3 py-2.5 whitespace-nowrap">{children}</th>;
}

function Td({ children, className = '' }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}

function Empty() {
  return <span className="text-neutral-300">—</span>;
}

function CardKV({ label, children }) {
  return (
    <div className="flex items-start gap-2">
      <div className="text-[9px] font-bold uppercase tracking-wider text-neutral-400 shrink-0 mt-0.5 w-24 truncate">{label}</div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
